import React from 'react';
import type {AppStore} from './state/store';
import {useAppStore} from './state/hooks';
import * as log from './util/log';
import {useApolloClient} from '@apollo/client';
import {NudgeCache} from './net/cache/init';
import {initEventStream} from './net/sse';

export type SSEProviderProps = {
  children?: React.ReactNode;
  source: string;
};

const SSEContext = React.createContext<boolean>(false);

/**
 * Hook to provide a server-sent events stream.
 */
const useSSEStream = (store: AppStore, cache: NudgeCache, source: string) => {
  React.useEffect(() => {
    const state = store.getState();
    if (!state.device.registered || !state.auth.loggedIn) {
      log.debug('Device not yet registered, not creating SSE stream');
      return;
    }

    const closeStream = initEventStream(source, cache);

    return () => closeStream();
  }, [store, source]);
};

/**
 * Ensure that an event stream is created and provided to the application.
 *
 * Note that the underlying stream is fully managed and is not exposed through
 * the context.
 */
export const SSEProvider = ({children, source}: SSEProviderProps) => {
  const store = useAppStore();
  const apolloClient = useApolloClient();
  useSSEStream(store, apolloClient.cache as NudgeCache, source);

  return <SSEContext.Provider value={true}>{children}</SSEContext.Provider>;
};
